import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
} from "@material-ui/core";
import MainInfo from "./MainInfo";
import mainStyles from "../WorkerManagementComponent/WorkerManagementComponent.module.css";
import styles from "../AgentSkillsManagement/styles.module.css";
import ConfirmComponent from "../ConfirmComponent/ConfirmComponent";
import { Alert } from "@material-ui/lab";
import { DeleteForever } from "@material-ui/icons";
import { GreenBtn, GreyInverseBtn } from "../Styled/StyledButtons";
import { useCallback, useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { ICustomSkill } from "../../models/AgentSkills/CustomSkill";
import {
  IExternalAgent,
  IDepartmentOption,
  ITaskChannel,
  emptyExternalAgent,
} from "./IExternalAgent";
import { TeamsActions } from "../../actions/TeamsActions";
import Departments from "./Departments";
import { DepartmentsActions } from "../../services/departments.actions";
import { AgentSkillsActions } from "../../actions/AgentSkillsActions";
import CustomSkills from "./CustomSkills";
import Activities from "./Activities";
import KPIs from "./KPIs";
import {
  ExternalAgentChannelsActions,
  ExternalAgentsActions,
  describeAgentError,
} from "../../actions/ExternalAgentsActions";
import TaskChannels from "./TaskChannels";
import history from "../history";

/**
 * Create and edit an external agent.
 *
 * One component serves /external-agent-create and /external-agent/id/:id, which is the
 * convention throughout this admin. It did not honour the second: it never read
 * match.params.id and never fetched the record, so the edit URL opened an empty create form,
 * and Apply — seeing no id in state — POSTed a duplicate instead of updating. Editing an
 * external agent has never been possible from this screen.
 *
 * Task Channels are loaded separately and only for an agent that already has a worker: they
 * live entirely in TaskRouter, keyed by the worker SID, so there is nothing to show until one
 * exists. Edits are held until Apply and written one PUT per changed channel, matching the
 * internal-agent screen — capacity is a routing control, and applying it the instant a digit
 * is typed would briefly publish "2" on the way to "20".
 */
const ExternalAgent = (props: RouteComponentProps<{ id?: string }>) => {
  const routeId = props.match.params.id;
  const agentId = routeId ? parseInt(routeId, 10) : undefined;

  const [externalAgent, setExternalAgent] = useState<IExternalAgent>(
    emptyExternalAgent()
  );
  // The record as last loaded or saved, so Cancel knows whether anything is at stake.
  const [pristine, setPristine] = useState<string>("");
  const [teams, setTeams] = useState<Array<{ Id: number; Name: string }>>([]);
  const [departments, setDepartments] = useState<Array<IDepartmentOption>>([]);
  const [customSkills, setCustomSkills] = useState<Array<ICustomSkill>>([]);
  const [taskChannels, setTaskChannels] = useState<Array<ITaskChannel>>([]);
  // SIDs edited since the last save. Only these are written back, so Apply does not rewrite
  // every channel on the worker each time.
  const [channelChanges, setChannelChanges] = useState<Array<string>>([]);
  const [alert, setAlert] = useState<{
    severity: "error" | "success" | "warning";
    text: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [backdrop, setBackdrop] = useState(true);
  const [discardConfirmShow, setDiscardConfirmShow] = useState(false);
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);

  // The initial fetch is four requests deep and an operator can leave before it lands —
  // Cancel, the sidebar, the browser Back button. Applying its result to an unmounted
  // component is a React warning and, on the create-then-redirect path, a write to state
  // that no longer exists.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const load = useCallback(async () => {
    setBackdrop(true);
    try {
      const [loadedTeams, loadedDepartments, loadedSkills] = await Promise.all([
        TeamsActions.getTeams(),
        DepartmentsActions.get(),
        AgentSkillsActions.getSkills(),
      ]);

      if (!mounted.current) {
        return;
      }

      setTeams(loadedTeams);
      // DepartmentsActions.get() hands back the raw JSON rows, so these are already
      // lowercase-keyed and match IDepartmentOption as they stand. It also swallows its own
      // errors into console.log and resolves undefined, hence the fallback.
      setDepartments(loadedDepartments || []);
      // getSkills() maps into the CustomSkill class, whose accessors are PascalCase. Read
      // through them rather than casting the class to the interface: the two really are
      // different shapes, and `as unknown as` would only hide it until one side changed.
      setCustomSkills(
        (loadedSkills || []).map((skill) => ({
          id: skill.Id,
          key: skill.Key,
          type_id: skill.TypeId,
        }))
      );

      if (agentId !== undefined && !isNaN(agentId)) {
        const agent = await ExternalAgentsActions.read(agentId);
        if (!mounted.current) {
          return;
        }
        setExternalAgent(agent);
        setPristine(JSON.stringify(agent));
        setChannelChanges([]);
        document.title = agent.name + " | Edit External Agent";

        // Only meaningful once a worker exists; the endpoint answers 409 otherwise, and a
        // failure here must not cost the operator the rest of the form.
        if (agent.sid) {
          try {
            const channels = await ExternalAgentChannelsActions.get(agentId);
            if (mounted.current) {
              setTaskChannels(channels);
            }
          } catch (channelError) {
            if (mounted.current) {
              setAlert({
                severity: "warning",
                text:
                  "Task channels could not be loaded: " +
                  describeAgentError(channelError),
              });
            }
          }
        }
      } else {
        const blank = emptyExternalAgent();
        setExternalAgent(blank);
        setPristine(JSON.stringify(blank));
        setTaskChannels([]);
        setChannelChanges([]);
        document.title = "Add External Agent";
      }
    } catch (error) {
      if (mounted.current) {
        setAlert({ severity: "error", text: describeAgentError(error) });
      }
    } finally {
      if (mounted.current) {
        setBackdrop(false);
      }
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  // Every edit goes through a functional update. The handlers are recreated each render and
  // close over that render's agent, so spreading the captured value drops any change made
  // between the render and the click — checking two boxes quickly, or a checkbox landing
  // while the save response is being applied.
  const handleTeam = (teamId: string) => {
    setExternalAgent((current) => ({
      ...current,
      team_id: teamId === "null" ? null : parseInt(teamId, 10),
    }));
  };

  const departmentsChangeHandle = (updatedDepartments: number[]): void => {
    setExternalAgent((current) => ({
      ...current,
      departments: updatedDepartments,
    }));
  };

  const customSkillsChangeHandle = (id: number, value: string): void => {
    setExternalAgent((current) => {
      // Rebuilt, not spliced. `[...skills]` copies the array but not the objects in it, so
      // the previous `updatedSkills[i].value = value` mutated the object the last render is
      // still holding.
      const exists = current.skills.some((s) => s.id === id);
      return {
        ...current,
        skills: exists
          ? current.skills.map((s) => (s.id === id ? { ...s, value } : s))
          : [...current.skills, { id, value }],
      };
    });
  };

  const customSkillRemoveHandle = (id: number): void => {
    setExternalAgent((current) => ({
      ...current,
      skills: current.skills.filter((s) => s.id !== id),
    }));
  };

  const handleBool = (name: string, checked: boolean) => {
    setExternalAgent((current) => ({
      ...current,
      [name]: checked,
    }));
  };

  const handleChangeAgent = (updatedAgent: IExternalAgent) => {
    setExternalAgent(updatedAgent);
  };

  const channelsChangeHandle = (updated: Array<ITaskChannel>): void => {
    const touched = updated
      .filter((channel, index) => {
        const before = taskChannels[index];
        return (
          before &&
          (before.capacity !== channel.capacity ||
            before.available !== channel.available)
        );
      })
      .map((channel) => channel.sid);

    setTaskChannels(updated);
    setChannelChanges((current) =>
      current.concat(touched.filter((sid) => current.indexOf(sid) === -1))
    );
  };

  /**
   * Writes the edited channels back, one PUT each — TaskRouter has no bulk update.
   *
   * Returns the channels that failed so Apply can say so instead of reporting a clean save;
   * a capacity the operator believes they set is worth more than a tidy success message.
   */
  const saveChannels = async (id: number): Promise<string[]> => {
    const failed: string[] = [];

    for (const sid of channelChanges) {
      const channel = taskChannels.find((c) => c.sid === sid);
      if (!channel) {
        continue;
      }
      try {
        await ExternalAgentChannelsActions.update(id, sid, {
          capacity: channel.capacity,
          available: channel.available,
        });
      } catch (error) {
        failed.push(channel.name || sid);
      }
    }

    return failed;
  };

  /**
   * Mirrors the server's required fields. Not a substitute for its validation — it saves the
   * operator a round trip to be told what the form already knows.
   */
  const validate = (): string[] => {
    const problems: string[] = [];

    if (externalAgent.name.trim() === "") {
      problems.push("Name is required.");
    }
    if (externalAgent.phone.trim() === "") {
      problems.push("Phone is required.");
    }
    externalAgent.skills.forEach((skill) => {
      const definition = customSkills.find((s) => s.id === skill.id);
      if (definition && definition.type_id === 2 && skill.value.trim() !== "" && isNaN(Number(skill.value))) {
        problems.push(`${definition.key} must be a number.`);
      }
    });

    return problems;
  };

  const updateAgent = async () => {
    const problems = validate();
    if (problems.length > 0) {
      setAlert({ severity: "error", text: problems.join(" ") });
      return;
    }

    // Set before the request, not in .finally — the button is only a guard against a second
    // click if it is disabled while the first one is in flight.
    setSaving(true);

    try {
      if (externalAgent.id) {
        const result = await ExternalAgentsActions.update(
          externalAgent.id,
          externalAgent
        );
        const failedChannels = await saveChannels(externalAgent.id);
        setChannelChanges([]);
        setExternalAgent(result.agent);
        setPristine(JSON.stringify(result.agent));

        if (failedChannels.length > 0) {
          setAlert({
            severity: "warning",
            text:
              "Saved, but these task channels did not update: " +
              failedChannels.join(", "),
          });
        } else {
          setAlert(
            result.twilioSynced === false
              ? {
                  severity: "warning",
                  text:
                    result.message ||
                    "Saved, but the Twilio worker could not be updated.",
                }
              : { severity: "success", text: "Saved!" }
          );
        }
      } else {
        const result = await ExternalAgentsActions.create(externalAgent);
        setExternalAgent(result.agent);
        setPristine(JSON.stringify(result.agent));
        setAlert({ severity: "success", text: "Saved!" });
        // Move to the edit URL so a second Apply updates this agent instead of creating
        // another one, and so a refresh lands on the record that was just made.
        history.replace("/external-agent/id/" + result.agent.id);
      }
    } catch (error) {
      const text = describeAgentError(error);
      setAlert({ severity: "error", text });
    } finally {
      setSaving(false);
    }
  };

  const deleteAgent = async (): Promise<void> => {
    setDeleteConfirmShow(false);

    if (externalAgent.id === undefined) {
      return;
    }

    setBackdrop(true);
    try {
      await ExternalAgentsActions.delete(externalAgent.id);
      history.push("/external-agents");
    } catch (error) {
      const text = describeAgentError(error);
      setAlert({ severity: "error", text });
      setBackdrop(false);
    }
  };

  const cancel = () => {
    if (JSON.stringify(externalAgent) !== pristine) {
      setDiscardConfirmShow(true);
      return;
    }
    history.push("/external-agents");
  };

  return (
    <>
      <Container maxWidth={false}>
        <Grid container spacing={1}>
          <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={2}></Grid>
              <Grid item xs={12} md={6}>
                <div className={mainStyles.Header}>
                  {externalAgent.id
                    ? "Edit External Agent"
                    : "Add External Agent"}
                </div>
                <div className={mainStyles.AgentName}>
                  {" "}
                  {externalAgent.name || "New External Agent"}
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <div className={styles.ButtonMargin}>
                  <label className={styles.FormLabel}>Agent Team</label>
                  <select
                    className={`${styles.FormControl}  ${styles.FullWidth}`}
                    value={
                      externalAgent.team_id === null
                        ? "null"
                        : String(externalAgent.team_id)
                    }
                    onChange={(event) => handleTeam(event.target.value)}
                  >
                    <option key={-1} value="null">
                      No Team
                    </option>
                    {teams.map((row) => (
                      <option key={row.Id} value={row.Id}>
                        {row.Name}
                      </option>
                    ))}
                  </select>
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <MainInfo agent={externalAgent} onChange={handleChangeAgent} />
                <Departments
                  departments={departments}
                  agentDepartments={externalAgent.departments}
                  onChange={departmentsChangeHandle}
                />
              </Grid>
              <Grid item xs={12} md={4} className={`${styles.FullHeight}`}>
                <Activities agent={externalAgent} handleChange={handleBool} />
                <CustomSkills
                  customSkills={customSkills}
                  agentSkills={externalAgent.skills}
                  onChange={customSkillsChangeHandle}
                  onRemove={customSkillRemoveHandle}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KPIs agent={externalAgent} handleChange={handleBool} />
                {taskChannels.length > 0 ? (
                  <TaskChannels
                    taskChannels={taskChannels}
                    onChange={channelsChangeHandle}
                  />
                ) : null}
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <div className={styles.ButtonMargin}>
                  <GreyInverseBtn onClick={cancel} variant="contained">
                    Cancel
                  </GreyInverseBtn>
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  // Shown only once the agent exists. The test was inverted, so Delete
                  // appeared on the create form — where there is nothing to delete — and was
                  // hidden on the edit form, where it is the point.
                  className={`${externalAgent.id ? "" : styles.DisplayNone}`}
                  color="secondary"
                  onClick={() => {
                    setDeleteConfirmShow(true);
                  }}
                >
                  <DeleteForever /> Delete Agent
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <div className={styles.ButtonMargin}>
                  <GreenBtn
                    disabled={saving}
                    className={mainStyles.floatRight}
                    onClick={updateAgent}
                    fullWidth={true}
                    variant="contained"
                    color="primary"
                  >
                    <CircularProgress
                      className={` ${styles.SaveProgress} ${
                        saving ? "" : styles.DisplayNone
                      }`}
                    />
                    &nbsp;&nbsp;Apply
                  </GreenBtn>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <ConfirmComponent
        show={discardConfirmShow}
        title="Discard changes"
        bodyText="Are you sure you want to discard unsaved changes?"
        close={() => setDiscardConfirmShow(false)}
        confirm={() => {
          setDiscardConfirmShow(false);
          history.push("/external-agents");
        }}
      ></ConfirmComponent>
      <Snackbar
        open={alert !== null}
        autoHideDuration={alert && alert.severity === "success" ? 5000 : 10000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setAlert(null)}
          severity={alert ? alert.severity : "success"}
          variant="filled"
        >
          <div className={styles.AlertError}>{alert ? alert.text : ""}</div>
        </Alert>
      </Snackbar>
      <Backdrop className={styles.Backdrop} open={backdrop}>
        <CircularProgress className={styles.BackdropProgress} />
        &nbsp;&nbsp;
        <p className={styles.BackdropProgress}>Loading...</p>
      </Backdrop>
      <ConfirmComponent
        show={deleteConfirmShow}
        title={"Delete Agent " + (externalAgent.name || "")}
        bodyText="This removes the agent and its Twilio worker. Are you sure?"
        close={() => setDeleteConfirmShow(false)}
        confirm={() => deleteAgent()}
      ></ConfirmComponent>
    </>
  );
};

export default ExternalAgent;
